/**
 * CreditBadge — iter1118
 *
 * Малък визуален индикатор, който показва колко кредита струва дадено
 * AI действие. Чете цените от публичния каталог `/api/pricing/catalog`
 * (`features_public[featureId].credit_cost`) и ги кешира за цялата сесия,
 * за да не прави мрежова заявка на всеки render.
 *
 * Използване:
 *     <CreditBadge featureId="lab_analysis" />                 → „⚡ 1 кредит"
 *     <CreditBadge featureId="voice_assistant" variant="pill"/> → различен стил
 *     <CreditBadge featureId="public_demo" />                   → „Безплатно"
 */
import React, { useEffect, useState } from "react";
import { Zap } from "lucide-react";

// Глобален кеш — попълва се от първия компонент, който се монтира.
let _featuresCache = null;
let _featuresLoadPromise = null;

async function _loadCatalog(API) {
  if (_featuresCache) return _featuresCache;
  if (_featuresLoadPromise) return _featuresLoadPromise;
  _featuresLoadPromise = (async () => {
    try {
      const r = await fetch(`${API}/pricing/catalog`);
      const data = await r.json();
      _featuresCache = data?.features || {};
      return _featuresCache;
    } catch {
      _featuresCache = {};
      return _featuresCache;
    } finally {
      _featuresLoadPromise = null;
    }
  })();
  return _featuresLoadPromise;
}

export function clearCreditBadgeCache() {
  _featuresCache = null;
}

export default function CreditBadge({
  featureId,
  fallbackCost = null,     // ако нямаме мета — показваме това число
  variant = "default",     // "default" | "pill" | "inline"
  className = "",
  showLabel = true,         // „1 кредит" vs само „1 ⚡"
}) {
  const [cost, setCost] = useState(fallbackCost);
  const [loaded, setLoaded] = useState(fallbackCost !== null);

  useEffect(() => {
    if (!featureId) return;
    // Опит за прочит от процеса на зареждане
    const API = (typeof window !== "undefined" && window.__NUTRAMILO_API)
      || `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
    _loadCatalog(API).then((features) => {
      if (features && features[featureId]) {
        setCost(features[featureId].credit_cost);
      }
      setLoaded(true);
    });
  }, [featureId]);

  if (!loaded && cost === null) return null;
  if (cost === null || cost === undefined) return null;

  // Представяне
  const free = cost === 0;
  const label = free ? "Безплатно" : `${cost} кредит${cost === 1 ? "" : "а"}`;

  const base = "inline-flex items-center gap-1 text-[11px] font-medium";
  const styles = {
    default: `${base} text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5`,
    pill:    `${base} text-white bg-amber-500 rounded-full px-2 py-0.5`,
    inline:  `${base} text-amber-700`,
  };
  const freeStyles = {
    default: `${base} text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5`,
    pill:    `${base} text-white bg-emerald-500 rounded-full px-2 py-0.5`,
    inline:  `${base} text-emerald-700`,
  };

  const cls = (free ? freeStyles : styles)[variant] || styles.default;

  return (
    <span className={`${cls} ${className}`}
          data-testid={`credit-badge-${featureId}`}
          title={free ? "Това действие не изразходва кредити." : `Струва ${cost} кредит${cost === 1 ? "" : "а"} от баланса.`}>
      <Zap size={11} fill={free ? "transparent" : "currentColor"} />
      {showLabel && <span>{label}</span>}
    </span>
  );
}
